import { FC, useEffect } from 'react';

import { Dialog, useDialog } from 'components';

import { useNewTransaction } from '..';
import { TransactionStatus } from '../new-transaction.context';

/**
 * Shown when a **bulk** save committed its transactions but one or more of their image uploads did
 * not.
 *
 * Reachable on the bulk path only. A single save now sends its attachments in the same request and
 * the server commits all of it or none, so there is no partial outcome there to report — that path
 * uses the failure dialog, which offers a retry. Bulk still fans out because one payload creates N
 * transactions that each need the same photos, which the API cannot express in a single body yet.
 *
 * Deliberately not the failure dialog: the records exist, and an operator who reads this as a
 * failed save would re-enter the gate transactions and create duplicates. It is also deliberately
 * not the success dialog, which is what let Rollbar #94 stay invisible for two months — every
 * failed upload was reported as "Transaction N created!".
 *
 * There is no retry action on purpose. The bulk attachment endpoint is not idempotent — image
 * objects are keyed on a fresh UUID rather than content — so a retry would duplicate whatever did
 * land.
 */
const TransactionPartialDialog: FC = () => {
  const { closeDialog, openDialog, ref } = useDialog();
  const { status, attachmentFailure, dismissAttachmentFailure } = useNewTransaction();

  useEffect(() => {
    if (!ref.current?.open && status === TransactionStatus.PARTIAL) openDialog();
    if (ref.current?.open && status !== TransactionStatus.PARTIAL) closeDialog();
  }, [ref, status, closeDialog, openDialog]);

  if (!attachmentFailure) return null;

  const { transactionIds, attempted, failed, draft } = attachmentFailure;
  const noun = draft ? 'Draft' : 'Transaction';
  const label = transactionIds.length > 1 ? `${noun}s ${transactionIds.join(', ')}` : `${noun} ${transactionIds[0]}`;

  return (
    <Dialog closeDialog={dismissAttachmentFailure} ref={ref} data-cy="partial-dialog">
      <div id="partial-dialog" className="d-flex flex-column g-3">
        <h3 className="text-info-dark font-weight-medium">Saved, but some attachments were not</h3>
        <p className="text-info-main">
          {label} {transactionIds.length > 1 ? 'were' : 'was'} saved, but <b>{failed}</b> of {attempted} attachments
          (photos, damages and tire inspections) failed to upload and {failed > 1 ? 'are' : 'is'} not stored.
        </p>
        <p className="text-info-main">
          The record is complete apart from those attachments — do not re-enter it. Report the missing attachments so
          they can be added.
        </p>
        <div className="grid-3 mt-3 justify-items-end">
          <button className="btn-primary col-start-3" type="button" onClick={dismissAttachmentFailure}>
            continue
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default TransactionPartialDialog;
